import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import Account, { type ShopRegistration } from '@/models/Account'
import Config from '@/utils/config'
import AccountService from '@/services/AccountService'
import { useNotify } from '@/composables/Notify'
import { useI18n } from 'vue-i18n'
import { Mutex } from 'async-mutex'

const cacheDays = 1

export const useAccountStore = defineStore(
  'account',
  () => {
    const { notifyError, notifySuccess } = useNotify()
    const { t } = useI18n()

    // state
    const accountId = ref<string|undefined>(undefined)
    const accounts = ref<Account[]>([])
    const fetchedAt = ref<number|undefined>(undefined)
    const loading = ref(false)
    const mutex = new Mutex()

    // getters
    const currentAccount = computed(() => accounts.value.find(a => a.id === accountId.value))
    const currentShop = computed(() => currentAccount.value?.variants?.[0])

    // actions
    const create = async (account: Partial<Account>, acceptSuisa: boolean): Promise<void> => {
      loading.value = true
      try {
        const response = await AccountService.create(shopRegistration(account, acceptSuisa))
        accounts.value = [...accounts.value, response]
        switchAccount(response.id)
        notifySuccess(t('common.createSuccess'))
      } catch (error) {
        notifyError(error)
        throw error
      } finally {
        loading.value = false
      }
    }

    const deleteItem = async (account_id: string): Promise<void> => {
      loading.value = true
      return AccountService.delete(account_id).then(() => {
        accounts.value = accounts.value.filter(a => a.id !== account_id)
        if (accountId.value === account_id) {
          accountId.value = accounts.value[0]?.id
        }
        notifySuccess(t('common.deleteSuccess'))
      }).catch(error => {
        notifyError(error)
        return Promise.reject(error)
      }).finally(() => {
        loading.value = false
      })
    }

    const fetch = (force = false) => {
      return mutex.runExclusive(async () => {
        if (loading.value) return
        if (!force && fetchedAt.value && ((new Date().getTime() - fetchedAt.value) < (cacheDays * 24 * 60 * 60 * 1000))) {
          return Promise.resolve(accounts.value)
        }

        loading.value = true
        return AccountService.list({ per_page: 1000 }).then(response => {
          accounts.value = response.data
          fetchedAt.value = Date.now().valueOf()
          // Every scoped request needs X-Account-Id, so never leave the
          // selection empty when the user has something to select.
          if (!currentAccount.value) {
            accountId.value = accounts.value[0]?.id
          }
        }).catch(error => {
          notifyError(error)
        }).finally(() => {
          loading.value = false
        })
      })
    }

    const shopRegistration = (account: Partial<Account>, acceptSuisa: boolean): ShopRegistration => {
      // Without a channel the backend would open a plain ad account: no shop,
      // no stream url, and nothing on screen to say why.
      if (!Config.instoreChannelId) {
        throw new Error('VITE_INSTORE_CHANNEL_ID is not configured')
      }

      return {
        ...account,
        instore_channel_id: Config.instoreChannelId,
        accept_suisa: acceptSuisa,
      }
    }

    const refresh = () => fetch(true)

    const switchAccount = (id: string) => {
      accountId.value = id
    }

    const update = (account: Partial<Account>): Promise<void> => {
      loading.value = true
      return AccountService.update(account).then(response => {
        const index = accounts.value.findIndex(c => c.id === response.id)
        if (index !== -1) {
          // The update answers with the account alone; its shop is unchanged.
          accounts.value = [
            ...accounts.value.slice(0, index),
            { ...response, variants: accounts.value[index].variants },
            ...accounts.value.slice(index + 1),
          ]
        } else {
          accounts.value = [...accounts.value, response]
        }
        notifySuccess(t('common.saveSuccess'))
      }).catch(error => {
        notifyError(error)
      }).finally(() => {
        loading.value = false
      })
    }

    return {
      accountId,
      accounts,
      create,
      currentAccount,
      currentShop,
      deleteItem,
      fetchedAt,
      fetch,
      loading,
      refresh,
      shopRegistration,
      switchAccount,
      update,
    }
  },
  {
    persist: import.meta.env.SSR ? false : {
      afterRestore: (context) => {
        context.store.loading = false
      },
    },
  }
)
